import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useDolfinClient } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import { formatCurrency, formatDate, formatDateTime } from "@/lib/format";
import type { components } from "@dolfin/api-public";
import { BackLink, PageContainer, PageHeader } from "@/components/PagePrimitives";
import { usePermissions } from "@/lib/permissions";
import { CreditNoteEInvoiceStatus, type CreditNoteDelivery } from "@/components/CreditNoteEInvoiceStatus";
import { CreditNoteStatusPill } from "@/components/CreditNotesTable";
import { InvoiceStatusPill } from "@/components/InvoicesListPage";
import { DeliveryStateBadge } from "@/components/EInvoiceDeliveryState";
import { networkLabel } from "@/components/SendPlanSummary";
import { EntityCell, ErrorState, HeaderRow, LoadingState, TH, TableShell, rowLink } from "@/components/PageUi";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";

type CreditNoteResponse = components["schemas"]["CreditNoteResponse"];
type InvoiceResponse = components["schemas"]["InvoiceResponse"];

export interface CreditNotePageProps {
  creditNoteId: string;
  /** Whether the client holds the AR e-invoicing module; without it the e-invoice section is not read. */
  eInvoicingEnabled?: boolean;
  /** Whether the organisation is registered for e-invoicing, or null while unknown. Gates the send/resend. */
  eInvoicingRegistered?: boolean | null;
  onBack?: () => void;
  onInvoiceClick?: (invoiceId: string) => void;
  onCustomerClick?: (customerId: string) => void;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </>
  );
}

/** A credit note credits exactly one invoice today, so "Linked invoice" is a table of one. */
export function CreditNotePage({ creditNoteId, eInvoicingEnabled = true, eInvoicingRegistered, onBack, onInvoiceClick, onCustomerClick }: CreditNotePageProps) {
  const api = useDolfinClient();
  const { can } = usePermissions();
  const [note, setNote] = useState<CreditNoteResponse | null>(null);
  const [invoice, setInvoice] = useState<InvoiceResponse | null>(null);
  const [delivery, setDelivery] = useState<CreditNoteDelivery | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data, error: apiError } = await api.GET("/v1/credit-notes/{creditNoteId}", {
      params: { path: { creditNoteId } },
    });
    if (apiError || !data) {
      setError(apiErrorMessage(apiError, "Credit note not found."));
      setLoading(false);
      return;
    }
    setNote(data);
    if (data.invoiceId) {
      const { data: inv } = await api.GET("/v1/invoices/{id}", { params: { path: { id: data.invoiceId } } });
      setInvoice(inv ?? null);
    }
    setLoading(false);
  }, [api, creditNoteId]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    void load();
  }, [load]);

  async function handleIssue() {
    if (!note?.invoiceId) return;
    setBusy(true);
    const { error: apiError } = await api.POST("/v1/invoices/{invoiceId}/credit-notes/{creditNoteId}/issue", {
      params: { path: { invoiceId: note.invoiceId, creditNoteId } },
    });
    setBusy(false);
    if (apiError) {
      toast.error(apiErrorMessage(apiError, "Failed to issue credit note."));
      return;
    }
    toast.success("Credit note issued");
    await load();
  }

  async function handleDelete() {
    if (!note?.invoiceId) return;
    setBusy(true);
    const { error: apiError } = await api.DELETE("/v1/invoices/{invoiceId}/credit-notes/{creditNoteId}", {
      params: { path: { invoiceId: note.invoiceId, creditNoteId } },
    });
    setBusy(false);
    if (apiError) {
      toast.error(apiErrorMessage(apiError, "Failed to delete credit note."));
      return;
    }
    toast.success("Draft deleted");
    onBack?.();
  }

  if (loading) {
    return (
      <PageContainer>
        <LoadingState label="Loading credit note..." />
      </PageContainer>
    );
  }
  if (error || !note) {
    return (
      <PageContainer>
        {onBack ? <BackLink onClick={onBack}>Back to credit notes</BackLink> : null}
        <ErrorState
          message={error || "That credit note could not be found."}
          onRetry={() => {
            setLoading(true);
            setError(null);
            void load();
          }}
        />
      </PageContainer>
    );
  }

  const isDraft = note.status === "Draft";
  const isIssued = note.status === "Issued";
  const rate = note.taxRateSnapshot;
  const customerName = invoice?.customer?.name;
  const creditedLine = note.invoiceLineItemId
    ? (invoice?.lineItems?.find((l) => l.id === note.invoiceLineItemId)?.description ?? "A line of the invoice")
    : "Whole invoice";

  return (
    <PageContainer>
      {onBack ? <BackLink onClick={onBack}>Back to credit notes</BackLink> : null}

      <PageHeader
        eyebrow="Credit note"
        title={<span className="font-mono">{note.documentNumber}</span>}
        subtitle={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span
              className={cn(
                "text-base font-semibold tabular-nums text-foreground",
                isIssued && "text-emerald-600 dark:text-emerald-400",
              )}
            >
              {isIssued ? "−" : ""}
              {formatCurrency(note.amount, note.currency)}
            </span>
            {customerName && invoice?.customerId ? (
              <>
                <span aria-hidden>·</span>
                <button
                  type="button"
                  className="hover:text-foreground hover:underline"
                  onClick={() => onCustomerClick?.(invoice.customerId!)}
                >
                  {customerName}
                </button>
              </>
            ) : null}
            {note.reason ? (
              <>
                <span aria-hidden>·</span>
                <span>{note.reason}</span>
              </>
            ) : null}
          </span>
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <CreditNoteStatusPill status={note.status ?? undefined} />
            {isIssued && note.invoiceId && (
              <CreditNoteEInvoiceStatus
                invoiceId={note.invoiceId}
                creditNoteId={creditNoteId}
                issuedAt={note.issuedAt}
                enabled={eInvoicingEnabled}
                canRetry={can("sendChase")}
                registered={eInvoicingRegistered}
                onDelivery={setDelivery}
              />
            )}
            {isDraft && (
              <>
                <Button size="sm" disabled={busy} onClick={handleIssue}>
                  {busy && <Spinner className="size-4" />}
                  Issue
                </Button>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button size="sm" variant="outline">
                      Delete
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-56">
                    <p className="mb-3 text-sm">Delete this draft credit note?</p>
                    <Button variant="destructive" className="w-full" disabled={busy} onClick={handleDelete}>
                      Delete
                    </Button>
                  </PopoverContent>
                </Popover>
              </>
            )}
          </div>
        }
      />

      <div className="flex flex-col gap-4">
        {/* What happens on Issue is the one thing worth knowing before pressing it. */}
        {isDraft && (
          <div className="rounded-xl border border-dashed bg-card/30 p-4 text-sm text-muted-foreground">
            This is a draft. It carries a placeholder number ({note.documentNumber}) until it is issued, when it
            takes the next number in your credit-note series and the credit is applied to the invoice.
          </div>
        )}

        <div className="grid gap-4 @3xl:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Amounts</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Net</span>
                <span className="tabular-nums">{formatCurrency(note.netAmount, note.currency)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  Tax {rate ? `at ${Number(rate.rate)}%` : "(zero-rated)"}
                </span>
                <span className="tabular-nums">{formatCurrency(note.taxAmount, note.currency)}</span>
              </div>
              <Separator className="my-2" />
              <div className="flex justify-between font-semibold">
                <span>Credit</span>
                <span className="tabular-nums">{formatCurrency(note.amount, note.currency)}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Details</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-[8rem_minmax(0,1fr)] gap-x-4 gap-y-2.5 text-sm">
                <Field label="Reason">{note.reason || "-"}</Field>
                <Field label="Credits">{creditedLine}</Field>
                <Field label="Tax rate">{rate ? `${rate.name} (${rate.code})` : "No tax"}</Field>
                <Field label="Currency">{note.currency}</Field>
                <Field label="Created">{formatDate(note.createdAt)}</Field>
                <Field label="Issued">{note.issuedAt ? formatDate(note.issuedAt) : "Not yet"}</Field>
              </dl>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Invoice credited</CardTitle>
          </CardHeader>
          <CardContent>
            <TableShell className="rounded-lg bg-transparent backdrop-blur-none">
              <Table>
                <TableHeader>
                  <HeaderRow>
                    <TableHead className={TH}>Invoice</TableHead>
                    <TableHead className={TH}>Status</TableHead>
                    <TableHead className={cn(TH, "text-right")}>Total</TableHead>
                    <TableHead className={cn(TH, "text-right")}>Credited</TableHead>
                    <TableHead className={cn(TH, "text-right")}>Outstanding</TableHead>
                  </HeaderRow>
                </TableHeader>
                <TableBody>
                  <TableRow
                    {...rowLink(note.invoiceId && onInvoiceClick ? () => onInvoiceClick(note.invoiceId!) : undefined)}
                  >
                    <TableCell>
                      <EntityCell
                        name={customerName}
                        fallback="Customer"
                        secondary={
                          <span className="font-mono">
                            {invoice?.documentNumber ?? "View invoice"}
                            {invoice?.issueDate ? ` · ${formatDate(invoice.issueDate)}` : ""}
                          </span>
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <InvoiceStatusPill status={invoice?.status ?? undefined} />
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCurrency(invoice?.totalAmount, invoice?.currency)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCurrency(invoice?.amountCredited, invoice?.currency)}
                    </TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">
                      {formatCurrency(invoice?.outstanding, invoice?.currency)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableShell>
          </CardContent>
        </Card>

        {isIssued && eInvoicingEnabled && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">E-invoice</CardTitle>
            </CardHeader>
            <CardContent className="text-sm">
              {!delivery ? (
                <p className="text-muted-foreground">
                  Not sent electronically. A credit note goes over the network automatically when its invoice was
                  delivered as an e-invoice; otherwise it is credited in your records only.
                </p>
              ) : (
                <div className="space-y-4">
                  <dl className="grid grid-cols-[8rem_minmax(0,1fr)] gap-x-4 gap-y-2.5 @3xl:grid-cols-[8rem_minmax(0,1fr)_8rem_minmax(0,1fr)]">
                    <Field label="Status">
                      <DeliveryStateBadge state={delivery.state} />
                    </Field>
                    <Field label="Network">
                      {networkLabel(delivery.network)}
                      {delivery.country ? ` · ${delivery.country}` : ""}
                      {delivery.sandbox ? " (test)" : ""}
                    </Field>
                    <Field label="Network id">
                      <span className="font-mono text-xs">{delivery.networkReference ?? "-"}</span>
                    </Field>
                    <Field label="Recipient">
                      <span className="font-mono text-xs">{delivery.recipientAddress ?? "-"}</span>
                    </Field>
                    <Field label="Delivered">
                      {delivery.deliveredAt ? formatDateTime(delivery.deliveredAt) : "-"}
                    </Field>
                    <Field label="Attempt">{delivery.attempt ?? "-"}</Field>
                  </dl>
                  {(delivery.faults ?? []).length > 0 && (
                    <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3">
                      <p className="font-medium text-destructive">What went wrong</p>
                      <ul className="mt-1 list-disc pl-5 text-muted-foreground">
                        {(delivery.faults ?? []).map((f, i) => (
                          <li key={i}>
                            {f.message}
                            {f.code ? <span className="text-xs"> ({f.code})</span> : null}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </PageContainer>
  );
}
